/**
 * In-memory cache + access state for the SCC coding JWT
 * from POST /coding-cases/access-context.
 *
 * Keep this module free of `@/api/*` imports so auth/logout (and other
 * early-loaded modules) can clear the cache without pulling scribeAPIV2.
 */

export type SccAccessStatus = 'idle' | 'ready' | 'denied' | 'error';

type CachedSccAccessContext = {
  accessToken: string;
  expiresAt: number;
};

type StatusListener = () => void;

let cachedContext: CachedSccAccessContext | null = null;
let accessStatus: SccAccessStatus = 'idle';
const statusListeners = new Set<StatusListener>();

function notifyStatusListeners(): void {
  for (const listener of statusListeners) {
    listener();
  }
}

function setAccessStatus(next: SccAccessStatus): void {
  if (accessStatus === next) return;
  accessStatus = next;
  notifyStatusListeners();
}

export function getSccAccessStatus(): SccAccessStatus {
  return accessStatus;
}

/** Subscribe to status transitions (useSyncExternalStore). */
export function subscribeSccAccessStatus(listener: StatusListener): () => void {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

/** Returns the cached token, or null if missing/expired. */
export function getCachedSccAccessToken(): string | null {
  if (!cachedContext) return null;
  if (Date.now() >= cachedContext.expiresAt * 1000) {
    cachedContext = null;
    return null;
  }
  return cachedContext.accessToken;
}

/** Clear token cache. Does not imply denied (use markSccAccessDenied). */
export function clearSccAccessContext(): void {
  cachedContext = null;
  if (accessStatus === 'ready') {
    // Keep ready so ensure/remint can recover without looking like no seat.
    return;
  }
  if (accessStatus !== 'denied' && accessStatus !== 'error') {
    setAccessStatus('idle');
  }
}

/** No coder_seat — coding JWT must not be requested. */
export function markSccAccessDenied(): void {
  cachedContext = null;
  setAccessStatus('denied');
}

/** coder_seat present but mint failed. */
export function markSccAccessError(): void {
  cachedContext = null;
  setAccessStatus('error');
}

/**
 * Clear a prior logout denial so a new authenticated entitlements fetch
 * can intentionally remint. In-flight mints that lose a logout race still
 * see denied after await and must not call setSccAccessContext.
 */
export function prepareSccAccessMint(): void {
  if (accessStatus === 'denied') {
    setAccessStatus('idle');
  }
}

/** Store token from a successful mint. */
export function setSccAccessContext(payload: {
  access_token?: string | null;
  expires_at?: number | null;
}): void {
  // Logout may have denied access while mint was in flight.
  if (accessStatus === 'denied') {
    return;
  }
  const accessToken = payload.access_token;
  const expiresAt = payload.expires_at;
  if (!accessToken || expiresAt == null) {
    markSccAccessError();
    return;
  }
  cachedContext = {
    accessToken,
    expiresAt,
  };
  setAccessStatus('ready');
}
